import { Link, useFetcher } from '@remix-run/react'
import {
	type Dispatch,
	type SetStateAction,
	useEffect,
	useState,
} from 'react'
import { cn } from '#app/utils/misc.js'
import { useOptionalUser } from '#app/utils/user.ts'
import { type Resource } from '../types'
import TagsBlock, { type Tag } from './TagsBlock'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Icon } from './ui/icon'

type Tagging = {
	Tag: Tag
	frequency: number
}

type FetcherData = {
	taggings?: Tagging[]
}

const curve =
	'absolute content-[""] dark:bg-gray-700 bg-gray-200 -bottom-2 left-1/2 curve'
const ResourceCard = ({
	resource,
	selectedResource,
	setSelectedResource,
	setValue,
	notSafe = false,
	favoritePage = false,
}: {
	resource: Resource
	selectedResource: number | null
	setSelectedResource: Dispatch<SetStateAction<number | null>>
	setValue?: Dispatch<SetStateAction<string>>
	notSafe: boolean
	favoritePage?: boolean
}) => {
	const { submit, state, data } = useFetcher<{
		data?: { resourceId: number }
	}>()
	const user = useOptionalUser()
	const fetcher = useFetcher<FetcherData>()
	const [localTags, setLocalTags] = useState<Tag[]>([])

	useEffect(() => {
		if (
			selectedResource === resource.id &&
			!fetcher.data &&
			fetcher.state === 'idle'
		) {
			fetcher.load(`/resource/${resource.id}/tags?notSafe=${notSafe}`)
		}
		if (fetcher.data?.taggings) {
			setLocalTags(fetcher.data.taggings.map(t => t.Tag))
		}
	}, [fetcher, resource.id, selectedResource, notSafe])

	const isSubmitting = state !== 'idle'
	const isFavorite = !!resource?.favorites?.length
	const favoriteId = resource?.favorites?.[0]?.id
	return (
		<>
			<Card
				className={`relative flex flex-col gap-4 ${selectedResource == resource.id ? `border-0 bg-gray-200 dark:bg-gray-700 ` : ''}`}
			>
				<div className="aspect-h-4 aspect-w-4 sm:aspect-none bg-gray-200 group-hover:opacity-75 sm:h-96">
					<img
						alt={`${resource.title}`}
						src={`https://fly.storage.tigris.dev/artaigo-bucket-v1/images/${resource.path}`}
						className="h-full w-full object-cover object-center sm:h-full sm:w-full"
					/>
				</div>
				<CardContent className="flex flex-1 flex-col justify-between gap-4">
					<div className="">
						{resource.title && (
							<p className="text-sm">
								<span className="font-semibold">Title:</span> {resource.title}
							</p>
						)}
						{resource.Artist?.name && (
							<p className="mt-1 text-sm">
								<span className="font-semibold">Artist:</span>{' '}
								{resource.Artist?.name}
							</p>
						)}
						{resource.location && (
							<p className="mt-1 text-sm">
								<span className="font-semibold">Location:</span>{' '}
								{resource.location}
							</p>
						)}
						{resource.institution && (
							<p className="mt-1 text-sm">
								<span className="font-semibold">Institution:</span>{' '}
								{resource.institution}
							</p>
						)}
					</div>

					<div className="flex flex-col gap-2">
						<Button
							className="w-full"
							onClick={() =>
								setSelectedResource(prev =>
									prev === resource.id ? null : resource.id,
								)
							}
						>
							{selectedResource === resource.id
								? 'hide tags'
								: 'show other tags'}
						</Button>
						<div className="flex gap-2">
							<Button className="w-full">
								<Link to={`/resource/${resource.id}`}>go to resource</Link>
							</Button>
							<Button
								disabled={!user}
								onClick={() =>
									favoritePage && favoriteId
										? submit(
												{
													id: favoriteId,
												},
												{ navigate: false, method: 'post' },
											)
										: submit(
												{
													resourceId: resource.id,
													type: isFavorite ? 'dislike' : 'like',
													...(isFavorite && favoriteId ? { favoriteId } : {}),
												},
												{ method: 'post' },
											)
								}
								className="w-full"
							>
								<Icon
									color={isFavorite || favoritePage ? 'tomato' : undefined}
									name={isFavorite || favoritePage ? 'heart-filled' : 'heart'}
									className={cn(
										' mr-2',
										isFavorite || (favoritePage && 'text-red'),
									)}
								/>
								{isFavorite || favoritePage ? 'Liked' : 'Like'}
								{isSubmitting && data?.data?.resourceId === resource.id && (
									<Icon name="update" className="ml-2 animate-spin" />
								)}
							</Button>
						</div>
					</div>
				</CardContent>
				<div className={selectedResource == resource.id ? curve : ''}></div>
			</Card>
			{selectedResource == resource.id && (
				<Card className="col-span-full border-none bg-gray-200 p-3 dark:bg-gray-700">
					<div className="text-sm italic text-gray-500">
						{fetcher.state === 'loading' ? (
							<div>Loading tags...</div>
						) : (
							<TagsBlock {...(setValue ? { setValue } : {})} tags={localTags} />
						)}
						{!fetcher.data?.taggings?.length && fetcher.state === 'idle' && (
							<div>
								{notSafe ? 'There are no tags.' : 'There are no safe tags.'}
							</div>
						)}
					</div>
				</Card>
			)}
		</>
	)
}

export default ResourceCard
